import type { AttributeOperator, RuleValueKind } from './attribute-rules';

/*
 * Every sentence the Server-side experiment setup shows, in one place so copy.test.ts can hold
 * it to the house rules (no em or en dashes). The type is "Server-side", the setting is
 * "Assignment", its options "Browser" and "Server" (decided 2026-10-26).
 */
export const COPY = {
  title: 'Assignment',
  /** Shown instead of the card to a user who cannot use server-side (PER-178), read-only. */
  assignedByServer: 'Visitors are assigned by your server.',
  description: 'Who decides which variant each visitor gets. Your own code shows the variant either way.',
  assignment: {
    browser: {
      label: 'Browser',
      description: 'The Pertento snippet picks a variant in the browser by weight. Your page code reads it there.',
    },
    server: {
      label: 'Server',
      description:
        'Your backend gets the variant from the Pertento SDK or the HTTP API and hands the decision to the page, so the first render is already right.',
    },
    visitorIdOff: 'Turn on the visitor ID for this website first, under Websites, Settings.',
    notDraft: 'Set while the experiment is a draft.',
  },
  flagKey: {
    label: 'Flag key',
    help: 'The name your code asks the SDK for. Lowercase letters, digits and hyphens, at most 64.',
    placeholder: 'new-checkout',
    invalid: 'Use lowercase letters, digits and hyphens, starting with a letter or digit.',
    save: 'Save key',
    notDraft: 'Set while the experiment is a draft.',
  },
  bucketOn: {
    label: 'Bucket on',
    help: 'The same ID always gets the same variant.',
    visitor: 'Visitor ID',
    customerId: 'Customer ID',
    visitorHelp:
      'The Pertento visitor ID, one per browser. Your backend must store it and send it back on every request. When it sends none, the response carries a new one and says so.',
    customerIdHelp:
      'Your own ID for a signed in customer, so they see one variant on every device. Send an opaque ID, not a sequential one.',
  },
  rules: {
    label: 'Attribute rules',
    help: 'Only visitors whose attributes match every rule are enrolled. Your backend passes the attributes to the SDK. With no rules, everyone is.',
    empty: 'No rules: every visitor is enrolled.',
    add: 'Add rule',
    full: 'An experiment has at most 20 rules.',
    remove: 'Remove rule',
    dialogTitle: 'Add attribute rule',
    dialogHelp: [
      'The rule is checked against the attributes your backend passes with each SDK call.',
      'A visitor without the attribute does not match, except that "exists" checks for it.',
    ],
    attribute: 'Attribute',
    attributePlaceholder: 'plan',
    operator: 'Operator',
    valueKind: 'Compare as',
    value: 'Value',
    listValue: 'Values, separated by commas',
  },
  snippets: {
    title: 'Use it in your code',
    sdkTitle: 'SDK',
    sdkDescription:
      'Install @pertentoai/sdk on your server, ask for the variant, then render the page for it. Keep the server key on the server.',
    seedTitle: 'Seed script',
    seedDescription:
      'Put the seed in the page before the Pertento script, so the browser records the visitor in the variant your server chose. The SDK writes it for you with seedScript().',
    curlTitle: 'HTTP',
    curlDescription:
      'Without the SDK, ask /sdk/decide directly. The response carries the decision and the seed to put in the page. When visitorIdMinted is true it made a new visitor ID: store it and send it back. Report conversions to /sdk/events with the flag keys the visitor was decided on.',
    missingKeys: 'Give every variant a key before you start: your code switches on it.',
    copyFailed: 'Could not copy. Select the code and copy it yourself.',
  },
  noBrowserTargeting:
    'Page targeting does not apply when your server assigns visitors. Use attribute rules for who is enrolled.',
  /** The server-side checklist at the top of the card: each step says what to do, ticked once done. */
  setup: {
    title: 'Set up',
    visitorId: {
      done: 'The visitor ID is on for this website.',
      todo: 'Turn on the visitor ID for this website, under Websites, Settings.',
    },
    serverKey: {
      done: 'This website has a server key. Your backend keeps it in its environment as PERTENTO_SERVER_KEY.',
      todo: 'Create a server key under Websites, Server keys. It is shown once: put it straight into your backend environment.',
      unknown:
        'Your backend needs a server key for this website. An Owner or Admin creates one under Websites, Server keys.',
    },
    flagKey: {
      done: 'The flag key is set.',
      todo: 'Give the experiment a flag key below.',
    },
    variantKeys: {
      done: 'Every variant has a key.',
      todo: 'Give every variant a key: Variants, the menu on each, Edit key and payload.',
    },
    code: 'Add the code below to your backend, and put the seed in the page before the Pertento script. Then start the experiment.',
    visitorHeader:
      'A single page app that calls your backend itself sends the visitor ID with each call, in the header x-pertento-visitor-id, from window.Pertento.visitorId.',
    conversions:
      'Conversions still come from the browser. To report them only from your backend, set the website tracking method to Server-side under Websites, Settings.',
    conversionsServer:
      'This website reports conversions only from your backend: call track() in the SDK, or POST /sdk/events. The browser reports none.',
  },
  /** Links to the public guides on pertento.ai (guides.ts). */
  guides: {
    setupGuide: 'Setup guide',
    how: 'How',
    guide: 'Guide',
    more: 'More guides',
  },
  /** Browser assignment: how the page's own code shows the variant the snippet picked. */
  browserSnippet: {
    title: 'Show the variant in your page',
    description:
      'The Pertento snippet picks a variant for each visitor. Each variant runs its own JavaScript when it applies, so the simplest way is to call your code from there, through Open Editor. Later, on a click or a route change, your code can also read the assignment.',
  },
} as const;

export const OPERATOR_LABELS: Record<AttributeOperator, string> = {
  equals: 'equals',
  in: 'is one of',
  'not in': 'is not one of',
  '<': 'is less than',
  '>': 'is greater than',
  exists: 'exists',
};

export const VALUE_KIND_LABELS: Record<RuleValueKind, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'True or false',
};
